import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  listCompanyUsers,
  listBonuses, createBonusApi, updateBonusApi, deleteBonusApi,
  exportBonusesRegister,
  listAllowances, createAllowanceApi, updateAllowanceApi, deleteAllowanceApi,
  listLocations,
} from "../api";
import { ROLES } from "../constants";
import { todayParts } from "../utils/dates";
import { computeExtraAmount } from "../controllers/extras";
import { listTaxes } from "../controllers/taxes";
import LocationFilter, { applyLocationFilter } from "../components/LocationFilter";
import EmployeeNameFilter, { filterUsersByName } from "../components/EmployeeNameFilter";
import AppPage from "../components/AppPage";

const MONTH_KEYS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const fmt = (n) => Number(n || 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 });

const BONUS_TYPES = [
  "monthly_bonus", "quarterly", "annual", "project",
  "piecework", "overtime", "material_aid", "gift", "other",
];

export default function Bonuses() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const [tab, setTab] = useState("bonuses");
  const tp = todayParts();
  const [year, setYear] = useState(tp.year);
  const [month, setMonth] = useState(tp.month);
  const [users, setUsers] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showAllowanceModal, setShowAllowanceModal] = useState(false);
  const [editing, setEditing] = useState(null); // existing row (bonus or allowance) for edit

  const taxes = useMemo(() => listTaxes(company?.tax_config), [company]);

  const isAdmin = user && [ROLES.DIRECTOR, ROLES.HR].includes(user.role);
  const isManager = user?.role === ROLES.MANAGER;
  const canCreate = isAdmin || isManager;

  // Allowed user_ids to act on (admin = everyone, manager = self + subordinates)
  const allowedUsers = useMemo(() => {
    if (!user) return [];
    let base;
    if (isAdmin) base = users;
    else if (isManager) base = users.filter(u => u.user_id === user.user_id || u.manager_id === user.user_id);
    else base = users.filter(u => u.user_id === user.user_id);
    return applyLocationFilter(base, locationFilter);
  }, [users, user, isAdmin, isManager, locationFilter]);

  const visibleUsers = useMemo(
    () => filterUsersByName(allowedUsers, nameFilter),
    [allowedUsers, nameFilter],
  );

  const reload = async () => {
    setLoading(true);
    const [u, bs, als, locs] = await Promise.all([
      listCompanyUsers(),
      listBonuses({ year, month }),
      listAllowances({ status: "all" }),
      listLocations().catch(() => []),
    ]);
    setUsers(u || []);
    setBonuses(bs || []);
    setAllowances(als || []);
    setLocations(locs || []);
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [year, month]);

  // Group bonuses by user
  const bonusesByUser = useMemo(() => {
    const m = new Map();
    for (const b of bonuses) {
      if (!m.has(b.user_id)) m.set(b.user_id, []);
      m.get(b.user_id).push(b);
    }
    return m;
  }, [bonuses]);

  // Group allowances by user (separate active/stopped later in render)
  const allowancesByUser = useMemo(() => {
    const m = new Map();
    for (const a of allowances) {
      if (!m.has(a.user_id)) m.set(a.user_id, []);
      m.get(a.user_id).push(a);
    }
    return m;
  }, [allowances]);

  const onSaveBonus = async (form) => {
    try {
      if (editing && editing._kind === "bonus") {
        await updateBonusApi(editing.bonus_id, form);
      } else {
        await createBonusApi(form);
      }
      setShowBonusModal(false); setEditing(null);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const onSaveAllowance = async (form) => {
    try {
      if (editing && editing._kind === "allowance") {
        await updateAllowanceApi(editing.allowance_id, form);
      } else {
        await createAllowanceApi(form);
      }
      setShowAllowanceModal(false); setEditing(null);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const onDeleteBonus = async (b) => {
    if (!window.confirm(t("bonuses.confirm_delete_bonus"))) return;
    try { await deleteBonusApi(b.bonus_id); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const onDeleteAllowance = async (a) => {
    if (!window.confirm(t("bonuses.confirm_delete_allowance"))) return;
    try { await deleteAllowanceApi(a.allowance_id); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const onExportBonuses = async () => {
    setExporting(true);
    try {
      await exportBonusesRegister(year, month);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <AppPage testId="bonuses-page" bodyScroll="scroll">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="fw-bold mb-0">
          <i className="bi bi-gift text-warning me-2"></i>
          {t("bonuses.title")}
        </h4>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          {(isAdmin || isManager) && (
            <EmployeeNameFilter value={nameFilter} onChange={setNameFilter} testid="bonuses-name-filter" />
          )}
          {isAdmin && (
            <LocationFilter locations={locations} users={users}
                            value={locationFilter} onChange={setLocationFilter}
                            testid="bonuses-location-filter" />
          )}
          {tab === "bonuses" && isAdmin && (
            <button type="button" className="btn btn-outline-success btn-sm"
                    onClick={onExportBonuses} disabled={exporting} data-testid="bonuses-export">
              <i className="bi bi-file-earmark-excel me-1"></i>
              {exporting ? t("common.loading") : t("bonuses.export_register")}
            </button>
          )}
          {tab === "bonuses" && (
            <>
              <select className="form-select" style={{ width: 160 }} value={month}
                      onChange={(e) => setMonth(Number(e.target.value))}
                      data-testid="bonuses-month-select">
                {MONTH_KEYS.map((k, i) => <option key={i} value={i}>{t(`months.${k}`)}</option>)}
              </select>
              <select className="form-select" style={{ width: 110 }} value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                      data-testid="bonuses-year-select">
                {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === "bonuses" ? "active" : ""}`}
                  onClick={() => setTab("bonuses")} data-testid="tab-bonuses">
            <i className="bi bi-cash-stack me-2"></i>
            {t("bonuses.tab_bonuses")}
            {bonuses.length > 0 && <span className="badge bg-secondary ms-2">{bonuses.length}</span>}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "allowances" ? "active" : ""}`}
                  onClick={() => setTab("allowances")} data-testid="tab-allowances">
            <i className="bi bi-plus-circle me-2"></i>
            {t("bonuses.tab_allowances")}
            {allowances.length > 0 && <span className="badge bg-secondary ms-2">{allowances.length}</span>}
          </button>
        </li>
      </ul>

      {tab === "bonuses" && (
        <BonusesTab
          loading={loading}
          users={visibleUsers}
          nameFilter={nameFilter}
          bonusesByUser={bonusesByUser}
          canCreate={canCreate}
          onAdd={() => { setEditing(null); setShowBonusModal(true); }}
          onEdit={(b) => { setEditing({ ...b, _kind: "bonus" }); setShowBonusModal(true); }}
          onDelete={onDeleteBonus}
          year={year} month={month}
          taxes={taxes}
          taxConfig={company?.tax_config}
        />
      )}

      {tab === "allowances" && (
        <AllowancesTab
          loading={loading}
          users={visibleUsers}
          nameFilter={nameFilter}
          allowancesByUser={allowancesByUser}
          canCreate={canCreate}
          onAdd={() => { setEditing(null); setShowAllowanceModal(true); }}
          onEdit={(a) => { setEditing({ ...a, _kind: "allowance" }); setShowAllowanceModal(true); }}
          onDelete={onDeleteAllowance}
          taxes={taxes}
          taxConfig={company?.tax_config}
        />
      )}

      {showBonusModal && (
        <BonusModal
          users={allowedUsers}
          year={year} month={month}
          initial={editing && editing._kind === "bonus" ? editing : null}
          onClose={() => { setShowBonusModal(false); setEditing(null); }}
          onSave={onSaveBonus}
          taxes={taxes}
        />
      )}
      {showAllowanceModal && (
        <AllowanceModal
          users={allowedUsers}
          initial={editing && editing._kind === "allowance" ? editing : null}
          onClose={() => { setShowAllowanceModal(false); setEditing(null); }}
          onSave={onSaveAllowance}
          taxes={taxes}
        />
      )}
    </AppPage>
  );
}

/* ----------------- BONUSES TAB ----------------- */
function BonusesTab({ loading, users, nameFilter, bonusesByUser, canCreate, onAdd, onEdit, onDelete, year, month, taxes, taxConfig }) {
  const { t } = useTranslation();
  if (loading) {
    return <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2"></div>{t("common.loading")}</div>;
  }
  const list = users
    .map(u => ({ u, items: bonusesByUser.get(u.user_id) || [] }))
    .filter(({ items }) => items.length > 0);

  return (
    <>
      {canCreate && (
        <button className="btn btn-primary mb-3" onClick={onAdd} data-testid="add-bonus-btn">
          <i className="bi bi-plus-circle me-2"></i>{t("bonuses.add_bonus")}
        </button>
      )}
      {list.length === 0 ? (
        <div className="card card-soft"><div className="card-body text-center py-5 text-muted">
          <i className="bi bi-gift fs-1 d-block mb-2"></i>
          {nameFilter?.trim()
            ? t("common.search_employee_empty")
            : t("bonuses.empty_bonuses", { month: t(`months.${MONTH_KEYS[month]}`), year })}
        </div></div>
      ) : (
        list.map(({ u, items }) => (
          <div key={u.user_id} className="card card-soft mb-3" data-testid={`bonus-user-${u.user_id}`}>
            <div className="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <strong>{u.name}</strong>
                <span className="text-muted small ms-2">{t(`roles.${u.role}`)}</span>
              </div>
              <div className="small text-muted">
                {t("bonuses.user_total")}: <strong>{fmt(items.reduce((s, b) => s + computeExtraAmount(b, u.salary, 0, taxConfig, u), 0))} ₸</strong>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t("bonuses.f_type")}</th>
                    <th>{t("bonuses.f_name")}</th>
                    <th className="text-end">{t("bonuses.f_amount")}</th>
                    <th>{t("bonuses.f_taxable")}</th>
                    <th>{t("bonuses.f_reason")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(b => {
                    const computed = computeExtraAmount(b, u.salary, 0, taxConfig, u);
                    const isNet = b.net_mode === true || (b.net_mode == null && u.salary_mode === "net");
                    return (
                      <tr key={b.bonus_id} data-testid={`bonus-row-${b.bonus_id}`}>
                        <td><span className="badge bg-info text-dark">{t(`bonuses.type_${b.type}`)}</span></td>
                        <td>{b.name}</td>
                        <td className="text-end fw-semibold">
                          {fmt(computed)} ₸
                          {b.amount_mode === "fixed" && isNet && (
                            <span className="badge bg-warning-subtle text-warning-emphasis ms-1"
                                  title={t("bonuses.net_mode_hint_net")}
                                  data-testid={`bonus-net-badge-${b.bonus_id}`}
                                  style={{ fontSize: 9 }}>
                              {t("bonuses.net_mode_short_net", { raw: fmt(b.amount) })}
                            </span>
                          )}
                          {b.amount_mode !== "fixed" && (
                            <div className="small text-muted">
                              {b.amount_mode === "percent_salary" && `${b.amount}% ${t("bonuses.of_salary")}`}
                              {b.amount_mode === "percent_gross" && `${b.amount}% ${t("bonuses.of_gross")}`}
                              {b.amount_mode === "piecework" && `${b.piecework_qty} × ${fmt(b.piecework_rate)}`}
                            </div>
                          )}
                        </td>
                        <td>
                          <TaxableBadges item={b} taxes={taxes} />
                        </td>
                        <td className="small text-muted">{b.reason || "—"}</td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-secondary me-1"
                                  onClick={() => onEdit(b)} data-testid={`edit-bonus-${b.bonus_id}`}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger"
                                  onClick={() => onDelete(b)} data-testid={`delete-bonus-${b.bonus_id}`}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </>
  );
}

/* ----------------- ALLOWANCES TAB ----------------- */
function AllowancesTab({ loading, users, nameFilter, allowancesByUser, canCreate, onAdd, onEdit, onDelete, taxes, taxConfig }) {
  const { t } = useTranslation();
  if (loading) {
    return <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2"></div>{t("common.loading")}</div>;
  }
  const list = users
    .map(u => ({ u, items: allowancesByUser.get(u.user_id) || [] }))
    .filter(({ items }) => items.length > 0);

  return (
    <>
      {canCreate && (
        <button className="btn btn-primary mb-3" onClick={onAdd} data-testid="add-allowance-btn">
          <i className="bi bi-plus-circle me-2"></i>{t("bonuses.add_allowance")}
        </button>
      )}
      {list.length === 0 ? (
        <div className="card card-soft"><div className="card-body text-center py-5 text-muted">
          <i className="bi bi-plus-circle fs-1 d-block mb-2"></i>
          {nameFilter?.trim() ? t("common.search_employee_empty") : t("bonuses.empty_allowances")}
        </div></div>
      ) : (
        list.map(({ u, items }) => (
          <div key={u.user_id} className="card card-soft mb-3" data-testid={`allowance-user-${u.user_id}`}>
            <div className="card-header bg-light">
              <strong>{u.name}</strong>
              <span className="text-muted small ms-2">{t(`roles.${u.role}`)}</span>
            </div>
            <div className="table-responsive">
              <table className="table table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t("bonuses.f_name")}</th>
                    <th className="text-end">{t("bonuses.f_amount")}</th>
                    <th>{t("bonuses.f_period")}</th>
                    <th>{t("bonuses.f_status")}</th>
                    <th>{t("bonuses.f_taxable")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(a => {
                    const computed = computeExtraAmount(a, u.salary, 0, taxConfig, u);
                    const isNet = a.net_mode === true || (a.net_mode == null && u.salary_mode === "net");
                    return (
                      <tr key={a.allowance_id} data-testid={`allowance-row-${a.allowance_id}`}>
                        <td>{a.name}</td>
                        <td className="text-end fw-semibold">
                          {fmt(computed)} ₸
                          {a.amount_mode === "fixed" && isNet && (
                            <span className="badge bg-warning-subtle text-warning-emphasis ms-1"
                                  title={t("bonuses.net_mode_hint_net")}
                                  data-testid={`allowance-net-badge-${a.allowance_id}`}
                                  style={{ fontSize: 9 }}>
                              {t("bonuses.net_mode_short_net", { raw: fmt(a.amount) })}
                            </span>
                          )}
                          {a.amount_mode === "percent_salary" && (
                            <div className="small text-muted">{a.amount}% {t("bonuses.of_salary")}</div>
                          )}
                        </td>
                        <td className="small">
                          {a.valid_from}
                          {a.valid_to ? ` → ${a.valid_to}` : ` → ${t("bonuses.indefinite")}`}
                        </td>
                        <td>
                          <span className={`badge ${a.status === "active" ? "bg-success" : "bg-secondary"}`}>
                            {t(`bonuses.status_${a.status}`)}
                          </span>
                        </td>
                        <td><TaxableBadges item={a} taxes={taxes} /></td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-secondary me-1"
                                  onClick={() => onEdit(a)} data-testid={`edit-allowance-${a.allowance_id}`}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger"
                                  onClick={() => onDelete(a)} data-testid={`delete-allowance-${a.allowance_id}`}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </>
  );
}

function TaxableBadges({ item, taxes }) {
  // item.non_taxable_keys is the source of truth. Fallback to legacy 4-flag layout.
  const nonTaxable = new Set(item.non_taxable_keys || (() => {
    const fallback = [];
    if (item.taxable_ipn === false) fallback.push("ipn");
    if (item.taxable_opv === false) fallback.push("opv");
    if (item.taxable_vosms === false) fallback.push("vosms");
    if (item.taxable_so === false) fallback.push("so");
    return fallback;
  })());
  const pill = (label, on) => (
    <span key={label} className={`badge me-1 ${on ? "bg-success" : "bg-light text-muted border"}`} style={{ fontSize: 10 }}>
      {label}
    </span>
  );
  return (
    <>{(taxes || []).map(t => pill(t.label, !nonTaxable.has(t.key)))}</>
  );
}

/* ----------------- BONUS MODAL ----------------- */
function BonusModal({ users, year, month, initial, onClose, onSave, taxes }) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState(initial ? [initial.user_id] : []);
  const [userSearch, setUserSearch] = useState("");
  const [type, setType] = useState(initial?.type || "monthly_bonus");
  const [name, setName] = useState(initial?.name || "");
  const [amountMode, setAmountMode] = useState(initial?.amount_mode || "fixed");
  const [amount, setAmount] = useState(initial?.amount ?? 0);
  const [qty, setQty] = useState(initial?.piecework_qty ?? 0);
  const [rate, setRate] = useState(initial?.piecework_rate ?? 0);
  const [reason, setReason] = useState(initial?.reason || "");
  // 3-state net mode for fixed amount: "inherit" | "net" | "gross"
  const initialNetMode = initial?.net_mode === true ? "net"
                        : initial?.net_mode === false ? "gross"
                        : "inherit";
  const [netMode, setNetMode] = useState(initialNetMode);

  // Universal taxability: { [taxKey]: bool }, default true (taxed).
  const initialTaxMap = useMemo(() => {
    const m = {};
    const nonTax = new Set(initial?.non_taxable_keys || (() => {
      if (!initial) return [];
      const f = [];
      if (initial.taxable_ipn === false) f.push("ipn");
      if (initial.taxable_opv === false) f.push("opv");
      if (initial.taxable_vosms === false) f.push("vosms");
      if (initial.taxable_so === false) f.push("so");
      return f;
    })());
    for (const x of (taxes || [])) m[x.key] = !nonTax.has(x.key);
    return m;
  }, [initial, taxes]);
  const [taxMap, setTaxMap] = useState(initialTaxMap);

  const isEdit = !!initial;

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, userSearch]);

  const toggleUser = (uid) => {
    setSelectedIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };
  const selectAll = () => setSelectedIds(filteredUsers.map(u => u.user_id));
  const clearAll = () => setSelectedIds([]);

  const setAllTaxes = (val) => {
    const m = {};
    for (const x of (taxes || [])) m[x.key] = val;
    setTaxMap(m);
  };
  const presetMatAid = () => { setAllTaxes(false); setType("material_aid"); };
  const presetGift   = () => { setAllTaxes(false); setType("gift"); };

  const canSave = name.trim().length > 0 && selectedIds.length > 0 &&
    (amountMode === "piecework" ? (Number(qty) > 0 && Number(rate) > 0) : Number(amount) >= 0);

  const submit = () => {
    if (!canSave) return;
    const non_taxable_keys = (taxes || []).filter(x => taxMap[x.key] === false).map(x => x.key);
    const net_mode = netMode === "inherit" ? null : (netMode === "net");
    const payload = {
      year, month,
      type, name: name.trim(), amount_mode: amountMode,
      amount: Number(amount) || 0,
      piecework_qty: Number(qty) || 0,
      piecework_rate: Number(rate) || 0,
      non_taxable_keys,
      net_mode,
      reason: reason.trim(),
    };
    if (isEdit) onSave(payload);
    else onSave({ ...payload, user_ids: selectedIds });
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-gift me-2 text-warning"></i>
              {isEdit ? t("bonuses.edit_bonus") : t("bonuses.add_bonus")}
            </h5>
            <button className="btn-close" onClick={onClose} data-testid="close-bonus-modal"></button>
          </div>
          <div className="modal-body">
            {!isEdit && (
              <div className="mb-3">
                <label className="form-label fw-semibold">{t("bonuses.f_employees")} <span className="text-danger">*</span></label>
                <div className="d-flex gap-2 mb-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={selectAll}>{t("bonuses.select_all")}</button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={clearAll}>{t("bonuses.clear_selection")}</button>
                  <span className="text-muted small align-self-center">{t("bonuses.selected_count", { n: selectedIds.length })}</span>
                </div>
                <input
                  className="form-control form-control-sm mb-2"
                  placeholder={t("common.search_employee_name")}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  data-testid="bonus-user-search"
                />
                <div style={{ maxHeight: 180, overflowY: "auto" }} className="border rounded p-2">
                  {filteredUsers.length === 0 ? (
                    <div className="text-muted small py-2">{t("common.search_employee_empty")}</div>
                  ) : filteredUsers.map(u => (
                    <div className="form-check" key={u.user_id}>
                      <input className="form-check-input" type="checkbox" id={`u-${u.user_id}`}
                             checked={selectedIds.includes(u.user_id)}
                             onChange={() => toggleUser(u.user_id)}
                             data-testid={`bonus-user-check-${u.user_id}`} />
                      <label className="form-check-label" htmlFor={`u-${u.user_id}`}>
                        {u.name} <span className="text-muted small">({t(`roles.${u.role}`)} · {Number(u.salary || 0).toLocaleString("ru-RU")} ₸)</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">{t("bonuses.f_type")}</label>
                <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}
                        data-testid="bonus-type-select">
                  {BONUS_TYPES.map(k => <option key={k} value={k}>{t(`bonuses.type_${k}`)}</option>)}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">{t("bonuses.f_name")} <span className="text-danger">*</span></label>
                <input className="form-control" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder={t("bonuses.name_placeholder")}
                       data-testid="bonus-name-input" />
              </div>

              <div className="col-md-6">
                <label className="form-label">{t("bonuses.f_amount_mode")}</label>
                <select className="form-select" value={amountMode}
                        onChange={(e) => setAmountMode(e.target.value)}
                        data-testid="bonus-mode-select">
                  <option value="fixed">{t("bonuses.mode_fixed")}</option>
                  <option value="percent_salary">{t("bonuses.mode_percent_salary")}</option>
                  <option value="percent_gross">{t("bonuses.mode_percent_gross")}</option>
                  <option value="piecework">{t("bonuses.mode_piecework")}</option>
                </select>
              </div>
              <div className="col-md-6">
                {amountMode === "piecework" ? (
                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label">{t("bonuses.f_qty")}</label>
                      <input type="number" className="form-control" value={qty}
                             onChange={(e) => setQty(e.target.value)} min="0" step="0.01"
                             data-testid="bonus-qty-input" />
                    </div>
                    <div className="col-6">
                      <label className="form-label">{t("bonuses.f_rate")} (₸)</label>
                      <input type="number" className="form-control" value={rate}
                             onChange={(e) => setRate(e.target.value)} min="0" step="0.01"
                             data-testid="bonus-rate-input" />
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="form-label">
                      {amountMode === "fixed" ? `${t("bonuses.f_amount")} (₸)` : `${t("bonuses.f_amount")} (%)`}
                    </label>
                    <input type="number" className="form-control" value={amount}
                           onChange={(e) => setAmount(e.target.value)} min="0" step="0.01"
                           data-testid="bonus-amount-input" />
                  </>
                )}
              </div>

              {amountMode === "fixed" && (
                <div className="col-md-6">
                  <label className="form-label">{t("bonuses.f_net_mode")}</label>
                  <select className="form-select" value={netMode}
                          onChange={(e) => setNetMode(e.target.value)}
                          data-testid="bonus-net-mode-select">
                    <option value="inherit">{t("bonuses.net_mode_inherit")}</option>
                    <option value="net">{t("bonuses.net_mode_net")}</option>
                    <option value="gross">{t("bonuses.net_mode_gross")}</option>
                  </select>
                  <div className="small text-muted mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    {netMode === "net" && t("bonuses.net_mode_hint_net")}
                    {netMode === "gross" && t("bonuses.net_mode_hint_gross")}
                    {netMode === "inherit" && t("bonuses.net_mode_hint_inherit")}
                  </div>
                </div>
              )}

              <div className="col-12">
                <label className="form-label">{t("bonuses.f_reason")}</label>
                <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder={t("bonuses.reason_placeholder")}
                       data-testid="bonus-reason-input" />
              </div>

              <div className="col-12">
                <label className="form-label d-flex justify-content-between align-items-center">
                  <span>{t("bonuses.f_taxable")}</span>
                  <span>
                    <button className="btn btn-sm btn-link p-0 me-3" onClick={presetMatAid} type="button">
                      {t("bonuses.preset_mataid")}
                    </button>
                    <button className="btn btn-sm btn-link p-0" onClick={presetGift} type="button">
                      {t("bonuses.preset_gift")}
                    </button>
                  </span>
                </label>
                <div className="d-flex flex-wrap gap-3">
                  {(taxes || []).map(x => (
                    <div className="form-check" key={x.key}>
                      <input className="form-check-input" type="checkbox" id={`btax-${x.key}`}
                        checked={taxMap[x.key] !== false}
                        onChange={(e) => setTaxMap(m => ({ ...m, [x.key]: e.target.checked }))}
                        data-testid={`bonus-tax-${x.key}`} />
                      <label className="form-check-label" htmlFor={`btax-${x.key}`}>{x.label}</label>
                    </div>
                  ))}
                </div>
                <div className="small text-muted mt-2">
                  <i className="bi bi-info-circle me-1"></i>
                  {t("bonuses.tax_hint")}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>{t("common.cancel")}</button>
            <button className="btn btn-primary" disabled={!canSave} onClick={submit}
                    data-testid="save-bonus-btn">
              <i className="bi bi-check2 me-1"></i>{t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------- ALLOWANCE MODAL ----------------- */
function AllowanceModal({ users, initial, onClose, onSave, taxes }) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState(initial ? [initial.user_id] : []);
  const [userSearch, setUserSearch] = useState("");
  const [name, setName] = useState(initial?.name || "");
  const [amountMode, setAmountMode] = useState(initial?.amount_mode || "fixed");
  const [amount, setAmount] = useState(initial?.amount ?? 0);
  const [validFrom, setValidFrom] = useState(initial?.valid_from || new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState(initial?.valid_to || "");
  const [status, setStatus] = useState(initial?.status || "active");
  const initialNetMode = initial?.net_mode === true ? "net"
                        : initial?.net_mode === false ? "gross"
                        : "inherit";
  const [netMode, setNetMode] = useState(initialNetMode);

  const initialTaxMap = useMemo(() => {
    const m = {};
    const nonTax = new Set(initial?.non_taxable_keys || (() => {
      if (!initial) return [];
      const f = [];
      if (initial.taxable_ipn === false) f.push("ipn");
      if (initial.taxable_opv === false) f.push("opv");
      if (initial.taxable_vosms === false) f.push("vosms");
      if (initial.taxable_so === false) f.push("so");
      return f;
    })());
    for (const x of (taxes || [])) m[x.key] = !nonTax.has(x.key);
    return m;
  }, [initial, taxes]);
  const [taxMap, setTaxMap] = useState(initialTaxMap);

  const isEdit = !!initial;

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, userSearch]);

  const toggleUser = (uid) => {
    setSelectedIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  const canSave = name.trim().length > 0 && selectedIds.length > 0 && validFrom &&
    Number(amount) >= 0 && (!validTo || validTo >= validFrom);

  const submit = () => {
    if (!canSave) return;
    const non_taxable_keys = (taxes || []).filter(x => taxMap[x.key] === false).map(x => x.key);
    const net_mode = netMode === "inherit" ? null : (netMode === "net");
    const payload = {
      name: name.trim(),
      amount_mode: amountMode,
      amount: Number(amount) || 0,
      valid_from: validFrom,
      valid_to: validTo || null,
      non_taxable_keys,
      net_mode,
    };
    if (isEdit) onSave({ ...payload, status });
    else onSave({ ...payload, user_ids: selectedIds });
  };

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-plus-circle me-2 text-primary"></i>
              {isEdit ? t("bonuses.edit_allowance") : t("bonuses.add_allowance")}
            </h5>
            <button className="btn-close" onClick={onClose} data-testid="close-allowance-modal"></button>
          </div>
          <div className="modal-body">
            {!isEdit && (
              <div className="mb-3">
                <label className="form-label fw-semibold">{t("bonuses.f_employees")} <span className="text-danger">*</span></label>
                <div className="d-flex gap-2 mb-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedIds(filteredUsers.map(u => u.user_id))}>
                    {t("bonuses.select_all")}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedIds([])}>
                    {t("bonuses.clear_selection")}
                  </button>
                  <span className="text-muted small align-self-center">{t("bonuses.selected_count", { n: selectedIds.length })}</span>
                </div>
                <input
                  className="form-control form-control-sm mb-2"
                  placeholder={t("common.search_employee_name")}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  data-testid="allowance-user-search"
                />
                <div style={{ maxHeight: 180, overflowY: "auto" }} className="border rounded p-2">
                  {filteredUsers.length === 0 ? (
                    <div className="text-muted small py-2">{t("common.search_employee_empty")}</div>
                  ) : filteredUsers.map(u => (
                    <div className="form-check" key={u.user_id}>
                      <input className="form-check-input" type="checkbox" id={`au-${u.user_id}`}
                             checked={selectedIds.includes(u.user_id)}
                             onChange={() => toggleUser(u.user_id)}
                             data-testid={`allowance-user-check-${u.user_id}`} />
                      <label className="form-check-label" htmlFor={`au-${u.user_id}`}>
                        {u.name} <span className="text-muted small">({t(`roles.${u.role}`)})</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="row g-3">
              <div className="col-md-12">
                <label className="form-label">{t("bonuses.f_name")} <span className="text-danger">*</span></label>
                <input className="form-control" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder={t("bonuses.allowance_name_placeholder")}
                       data-testid="allowance-name-input" />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t("bonuses.f_amount_mode")}</label>
                <select className="form-select" value={amountMode}
                        onChange={(e) => setAmountMode(e.target.value)}
                        data-testid="allowance-mode-select">
                  <option value="fixed">{t("bonuses.mode_fixed")}</option>
                  <option value="percent_salary">{t("bonuses.mode_percent_salary")}</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  {amountMode === "fixed" ? `${t("bonuses.f_amount")} (₸)` : `${t("bonuses.f_amount")} (%)`}
                </label>
                <input type="number" className="form-control" value={amount}
                       onChange={(e) => setAmount(e.target.value)} min="0" step="0.01"
                       data-testid="allowance-amount-input" />
              </div>

              {amountMode === "fixed" && (
                <div className="col-md-6">
                  <label className="form-label">{t("bonuses.f_net_mode")}</label>
                  <select className="form-select" value={netMode}
                          onChange={(e) => setNetMode(e.target.value)}
                          data-testid="allowance-net-mode-select">
                    <option value="inherit">{t("bonuses.net_mode_inherit")}</option>
                    <option value="net">{t("bonuses.net_mode_net")}</option>
                    <option value="gross">{t("bonuses.net_mode_gross")}</option>
                  </select>
                  <div className="small text-muted mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    {netMode === "net" && t("bonuses.net_mode_hint_net")}
                    {netMode === "gross" && t("bonuses.net_mode_hint_gross")}
                    {netMode === "inherit" && t("bonuses.net_mode_hint_inherit")}
                  </div>
                </div>
              )}

              <div className="col-md-6">
                <label className="form-label">{t("bonuses.f_valid_from")} <span className="text-danger">*</span></label>
                <input type="date" className="form-control" value={validFrom}
                       onChange={(e) => setValidFrom(e.target.value)}
                       data-testid="allowance-valid-from" />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t("bonuses.f_valid_to")}</label>
                <input type="date" className="form-control" value={validTo}
                       onChange={(e) => setValidTo(e.target.value)}
                       data-testid="allowance-valid-to" />
                <div className="small text-muted">{t("bonuses.valid_to_hint")}</div>
              </div>

              {isEdit && (
                <div className="col-md-6">
                  <label className="form-label">{t("bonuses.f_status")}</label>
                  <select className="form-select" value={status}
                          onChange={(e) => setStatus(e.target.value)}
                          data-testid="allowance-status-select">
                    <option value="active">{t("bonuses.status_active")}</option>
                    <option value="stopped">{t("bonuses.status_stopped")}</option>
                  </select>
                </div>
              )}

              <div className="col-12">
                <label className="form-label">{t("bonuses.f_taxable")}</label>
                <div className="d-flex flex-wrap gap-3">
                  {(taxes || []).map(x => (
                    <div className="form-check" key={x.key}>
                      <input className="form-check-input" type="checkbox" id={`atax-${x.key}`}
                        checked={taxMap[x.key] !== false}
                        onChange={(e) => setTaxMap(m => ({ ...m, [x.key]: e.target.checked }))}
                        data-testid={`allowance-tax-${x.key}`} />
                      <label className="form-check-label" htmlFor={`atax-${x.key}`}>{x.label}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>{t("common.cancel")}</button>
            <button className="btn btn-primary" disabled={!canSave} onClick={submit}
                    data-testid="save-allowance-btn">
              <i className="bi bi-check2 me-1"></i>{t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
